import React, { useCallback, useEffect, useRef, useState } from "react";
import { IconButton, RoundedButton } from "@bloomberg/media-ui-core";
import { Lightbulb } from "@bloomberg/media-ui-iconography";
import { Overlay, Popover } from "@bloomberg/media-ui-overlay";
import { useFeatureGate } from "@foundation/statsig";

import { TranslatedMessage, useTranslateMessage } from "~utils/i18n/translate";

import styles from "./PreferUs.module.css";

const PREFER_US_KEY = "preferUsOnGoogleDismissed";

function trackPreferUsAction({ eventLabel }: { eventLabel: string }) {
    window.dataLayer?.push({
        event: "article_social_share",
        eventCategory: "social",
        eventAction: "click",
        eventLabel,
    });
}

// [ENG4WAM-107] - will move to media-ui-iconography when less urgent
const PreferenceSVG = React.forwardRef<
    SVGSVGElement,
    { className?: string; fillColor?: string }
>(({ className, fillColor = "#262626" }, ref) => (
    <svg
        ref={ref}
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        className={className}
    >
        <rect
            x="0.5"
            y="6.50354"
            width="13.3953"
            height="13.3953"
            rx="2.32558"
            transform="rotate(-7.60561 0.5 6.50354)"
            fill={fillColor}
        />
        <rect
            x="4.40702"
            y="1.52132"
            width="14.7907"
            height="14.7907"
            rx="3.02325"
            fill={fillColor}
            stroke="white"
            strokeWidth="1.39535"
        />
        <path
            d="M10.6996 6.28292H11.6977C12.6094 6.28292 13.0029 6.58121 13.0029 7.18775C13.0029 7.71472 12.5998 8.07269 11.8033 8.07269H10.7092V6.28292H10.6996ZM9.5 12.219H11.9376C13.214 12.219 14.5 11.7417 14.5 10.2204C14.5 9.31557 13.8858 8.62952 13.0413 8.53008V8.51018C13.7418 8.29144 14.2121 7.76444 14.2121 6.97894C14.2121 5.7062 13.166 5.21899 12.1391 5.21899H9.5V12.219ZM10.6996 9.13662H11.8512C12.8301 9.13662 13.2812 9.39512 13.2812 10.1309C13.2812 11.0556 12.4175 11.165 11.8033 11.165H10.6996V9.13662Z"
            fill="white"
        />
    </svg>
));

PreferenceSVG.displayName = "PreferenceSVG";

type Props = {
    hideText?: boolean;
    vertical: boolean;
};

export default function PreferUsButton({
    hideText,
    vertical,
}: Props): JSX.Element | null {
    const [showPreferUsOverlay, setShowPreferUsOverlay] = useState(false);
    const [showPreferUsPopover, setShowPreferUsPopover] = useState(false);
    const [preferUsIconRef, setPreferUsIconRef] =
        useState<SVGSVGElement | null>(null);
    const googlePreferenceElementRef = useRef<HTMLDivElement>(null);
    const featureGate = useFeatureGate("prefer_us_on_google");
    const translateMessage = useTranslateMessage();

    useEffect(() => {
        const hasSeenPreferUsPopover = localStorage.getItem(PREFER_US_KEY);

        setShowPreferUsPopover(!Boolean(hasSeenPreferUsPopover));
    }, []);

    const onPreferUsClick = useCallback(() => {
        setShowPreferUsOverlay(true);
        trackPreferUsAction({ eventLabel: "open_preferred_source_overlay" });
        localStorage.setItem(PREFER_US_KEY, `${Date.now()}`);
        setShowPreferUsPopover(false);
    }, []);

    const onOverlayExitClick = useCallback(() => {
        setShowPreferUsOverlay(false);
        trackPreferUsAction({
            eventLabel: "close_preferred_source_overlay",
        });
    }, []);

    if (!Boolean(featureGate.value)) {
        return null;
    }

    const isIcon = hideText ?? vertical;

    const iconButtonAdditionalClassname = vertical
        ? styles.verticalIcon
        : styles.horizontalIcon;

    return (
        <>
            <div ref={googlePreferenceElementRef}>
                {isIcon ? (
                    <IconButton
                        variant="outlined"
                        className={`${styles.preferUsIconButton} ${isIcon ? iconButtonAdditionalClassname : ""}`}
                        onClick={onPreferUsClick}
                    >
                        <PreferenceSVG
                            ref={setPreferUsIconRef}
                            className={`${styles.preferUsIcon} ${styles.iconOnly}`}
                        />
                    </IconButton>
                ) : (
                    <RoundedButton
                        className={styles.preferUsTextButton}
                        onClick={onPreferUsClick}
                    >
                        <PreferenceSVG
                            ref={setPreferUsIconRef}
                            className={styles.preferUsIcon}
                        />
                        <TranslatedMessage
                            id="addOnGoogle"
                            defaultMessage="Add us on Google"
                        />
                    </RoundedButton>
                )}
            </div>
            {showPreferUsOverlay && (
                <Overlay
                    headerText={translateMessage({
                        id: "googleReporting",
                        defaultMessage:
                            "Get more Bloomberg reporting in your search results",
                    })}
                    anchorElementRef={googlePreferenceElementRef}
                    placement="bottom-start"
                    onExit={onOverlayExitClick}
                >
                    <span className={styles.overlayText}>
                        <TranslatedMessage
                            id="googlePreferredSource"
                            defaultMessage="You can now set Bloomberg as your preferred source on Google."
                        />
                    </span>
                    <div className={styles.overlayButtonContainer}>
                        <a
                            href="https://www.google.com/preferences/source?q=bloomberg.com"
                            target="_blank"
                            onClick={() =>
                                trackPreferUsAction({
                                    eventLabel: "google_preferred_source",
                                })
                            }
                        >
                            <RoundedButton colorMode="dark">
                                <TranslatedMessage
                                    id="addOnGoogle"
                                    defaultMessage="Add us on Google"
                                />
                            </RoundedButton>
                        </a>
                        <a
                            href="https://www.bloomberg.com/help/question/adding-bloomberg-as-your-preferred-source-on-google-search/"
                            target="_blank"
                            onClick={() =>
                                trackPreferUsAction({
                                    eventLabel: "preferred_learn_more",
                                })
                            }
                        >
                            <RoundedButton>
                                <TranslatedMessage
                                    id="learnMore"
                                    defaultMessage="Learn More"
                                />
                            </RoundedButton>
                        </a>
                    </div>
                </Overlay>
            )}
            {showPreferUsPopover && (
                <Popover
                    Icon={<Lightbulb />}
                    eyebrow={translateMessage({
                        id: "addOnGoogle",
                        defaultMessage: "Add us on Google",
                    })}
                    anchorElement={preferUsIconRef}
                    allowedPlacements={["bottom-start", "bottom", "bottom-end"]}
                    onDismiss={() =>
                        localStorage.setItem(PREFER_US_KEY, `${Date.now()}`)
                    }
                    text={translateMessage({
                        id: "chooseBloombergPreferred",
                        defaultMessage:
                            "Choose Bloomberg as a preferred news source to see more of our reporting on Google",
                    })}
                />
            )}
        </>
    );
}
